import {
  cloneElement,
  forwardRef,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";

import { DisplayState } from "@/constants/state";
import {
  ICON_BUTTON_DEFAULTS,
  ICON_BUTTON_SIZE_CLASS_MAP,
  ICON_BUTTON_VARIANT_CLASS_MAP,
  type TIconButtonProps,
} from "@/ui/components/primitives/IconButton";
import { Icon } from "@/ui/components/primitives/Icon";
import { Spinner } from "@/ui/components/primitives/Spinner";
import { cn } from "@/ui/lib/utils";

type TIconButtonChildProps = {
  children?: ReactNode;
  className?: string;
  "data-component"?: string;
  ref?: Ref<HTMLElement>;
};

export const IconButton = forwardRef<HTMLElement, TIconButtonProps>(
  (
    {
      asChild = ICON_BUTTON_DEFAULTS.asChild,
      className,
      children,
      icon,
      size = ICON_BUTTON_DEFAULTS.size,
      state,
      type = "button",
      variant = ICON_BUTTON_DEFAULTS.variant,
      ...props
    },
    ref,
  ) => {
    const iconButtonClassName = cn(
      "inline-flex shrink-0 items-center justify-center",
      "rounded-sys-md",
      "border-solid",
      "transition-all duration-200",
      "focus-visible:outline-none focus-visible:ring-2",
      "disabled:pointer-events-none disabled:opacity-50",
      "[&_svg]:pointer-events-none [&_svg]:shrink-0",
      ICON_BUTTON_VARIANT_CLASS_MAP[variant],
      ICON_BUTTON_SIZE_CLASS_MAP[size],
      className,
    );

    const iconButtonContent = state === DisplayState.LOADING ? (
      <Spinner />
    ) : (
      <Icon icon={icon} aria-hidden="true" />
    );

    if (asChild && isValidElement(children)) {
      const child = children as ReactElement<TIconButtonChildProps>;

      return cloneElement(child, {
        ...props,
        ref,
        "data-component": "IconButton",
        className: cn(iconButtonClassName, child.props.className),
        children: iconButtonContent,
      });
    }

    return (
      <button
        {...props}
        ref={ref as Ref<HTMLButtonElement>}
        type={type}
        data-component="IconButton"
        className={iconButtonClassName}
        disabled={state === DisplayState.LOADING || props.disabled}
      >
        { iconButtonContent }
      </button>
    );
  },
);

IconButton.displayName = "IconButton";
